import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PACKAGES, CONTACT } from "@/data/content";
import { CheckCircle, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

const INTERESTS = [
  ...PACKAGES.map((p) => `${p.name} Package`),
  "Custom Package",
  "Individual Service",
  "Not sure yet",
];

export default function Consultation() {
  const [form, setForm] = useState({
    name: "", email: "", business: "", phone: "",
    interest: "", budget: "", preferred_date: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.business || !form.message) {
      setError("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/consultations", form);
      setDone(true);
      toast.success("Consultation request sent — we'll be in touch!");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-[#6B7280] text-[#F6F4F0] py-3 px-1 placeholder:text-[#6B7280] focus:border-[#C8A96A] focus:outline-none transition-colors duration-300";

  return (
    <div className="bg-navy min-h-screen">
      <Header />
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-36 pb-28">
        <Link to="/" data-testid="back-home-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#6B7280] hover:text-gold transition-colors mb-16">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">Book a Consultation</p>
            <h1 className="font-heading font-light text-4xl sm:text-5xl lg:text-6xl text-[#F6F4F0] leading-tight mb-8">
              Let's design your growth plan.
            </h1>
            <p className="text-[#E5E7EB] font-light leading-relaxed mb-10">
              Tell us about your brand and we'll set up a meeting. We'll confirm your date by email, then finalize a custom plan and pricing together. In-person shoots are scheduled after we meet (2:30–7:30).
            </p>
            <div className="hairline pt-8">
              <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-3">Prefer email?</p>
              <a href={`mailto:${CONTACT.email}`} className="text-gold hover:text-[#F6F4F0] transition-colors break-all">{CONTACT.email}</a>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <motion.div
                data-testid="consultation-success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-[#C8A96A]/40 bg-[#101f30] p-12 text-center"
              >
                <CheckCircle size={56} weight="thin" className="text-gold mx-auto mb-6" />
                <h2 className="font-heading text-3xl text-[#F6F4F0] mb-4">Request received.</h2>
                <p className="text-[#E5E7EB] font-light max-w-md mx-auto">
                  Thank you, {form.name.split(" ")[0]}. We've got your details and will email you at {form.email} to confirm your meeting.
                </p>
                <Link to="/" className="inline-block mt-8 text-xs uppercase tracking-[0.2em] px-8 py-4 border border-[#C8A96A] text-gold hover:bg-[#C8A96A] hover:text-[#0D1B2A] transition-colors">
                  Back to Home
                </Link>
              </motion.div>
            ) : (
              <form data-testid="consultation-form" onSubmit={submit} className="space-y-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280] block mb-2">Full Name *</label>
                    <input data-testid="input-name" className={inputClass} value={form.name} onChange={update("name")} placeholder="Jane Doe" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280] block mb-2">Email *</label>
                    <input data-testid="input-email" type="email" className={inputClass} value={form.email} onChange={update("email")} placeholder="jane@brand.com" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280] block mb-2">Business Name *</label>
                    <input data-testid="input-business" className={inputClass} value={form.business} onChange={update("business")} placeholder="Your Brand" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280] block mb-2">Phone</label>
                    <input data-testid="input-phone" className={inputClass} value={form.phone} onChange={update("phone")} placeholder="(555) 000-0000" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280] block mb-2">Interested In</label>
                    <select data-testid="input-interest" className={`${inputClass} appearance-none cursor-pointer`} value={form.interest} onChange={update("interest")}>
                      <option value="" className="bg-[#0D1B2A]">Select…</option>
                      {INTERESTS.map((o) => <option key={o} value={o} className="bg-[#0D1B2A]">{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280] block mb-2">Preferred Meeting Date</label>
                    <input data-testid="input-date" type="date" className={`${inputClass} [color-scheme:dark]`} value={form.preferred_date} onChange={update("preferred_date")} />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280] block mb-2">Monthly Budget (optional)</label>
                  <input data-testid="input-budget" className={inputClass} value={form.budget} onChange={update("budget")} placeholder="e.g. $750/month" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280] block mb-2">Tell us about your goals *</label>
                  <textarea data-testid="input-message" rows={4} className={`${inputClass} resize-none`} value={form.message} onChange={update("message")} placeholder="What are you hoping to achieve?" />
                </div>

                {error && <p data-testid="consultation-error" className="text-red-400 text-sm">{error}</p>}

                <button
                  data-testid="consultation-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto text-xs uppercase tracking-[0.2em] px-12 py-5 bg-[#C8A96A] text-[#0D1B2A] hover:bg-[#F6F4F0] transition-colors duration-300 font-medium disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Request Consultation"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
